const VISITOR_KEY_STORAGE_KEY = "cos_visitor_key";

function randomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/**
 * The widget's one piece of durable client-side identity, persisted across
 * reloads and navigations (PLAN.md "Session & visitor tracking"). Falls
 * back to a session-only id rather than failing to load when storage is
 * blocked (private mode, restrictive iframe policy, ...).
 */
export function getOrCreateVisitorKey(): string {
  try {
    const existing = localStorage.getItem(VISITOR_KEY_STORAGE_KEY);
    if (existing) return existing;
    const created = randomId();
    localStorage.setItem(VISITOR_KEY_STORAGE_KEY, created);
    return created;
  } catch {
    return randomId();
  }
}

export interface MintSessionInput {
  apiUrl: string;
  publicKey: string;
  visitorKey: string;
  entryUrl?: string;
}

export async function mintWidgetSession(input: MintSessionInput): Promise<string> {
  const response = await fetch(`${input.apiUrl}/api/widget/session`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ publicKey: input.publicKey, visitorKey: input.visitorKey, entryUrl: input.entryUrl }),
  });
  if (!response.ok) {
    throw new Error(`failed to start chat session (${response.status})`);
  }
  const body = (await response.json()) as { token: string };
  return body.token;
}

/**
 * How the session token is obtained. A client site has a public key and the
 * widget mints with it; the agency panel's live preview already holds one
 * minted for it server-side (it has no public key — the panel's origin is
 * on no bot key's allowlist) and hands it over instead.
 */
export type WidgetSessionSource = { publicKey: string } | { mintSession: () => Promise<string> };

/**
 * The only place the two differ. Everything past this call — socket,
 * protocol, rendering — is one path, because a preview that took a
 * different one would stop being a preview.
 */
export function resolveSessionToken(source: WidgetSessionSource, input: { apiUrl: string; entryUrl?: string }): Promise<string> {
  if ("mintSession" in source) return source.mintSession();
  return mintWidgetSession({
    apiUrl: input.apiUrl,
    publicKey: source.publicKey,
    visitorKey: getOrCreateVisitorKey(),
    entryUrl: input.entryUrl,
  });
}
